import React, { useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DialogStep } from '@modernloop/shared/components';
import { CircularProgress } from '@mui/material';

import {
  CreateTaskDialogPage_ApplicationFragment,
  CreateTaskDialogPage_InterviewPlanFragment,
  TaskCreationSource,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import cloneInterviewPlan from 'src/entities/InterviewPlan/cloneInterviewPlan';
import useInterviewPlanEntityHasInterviewHiddenFromCandidate from 'src/entities/InterviewPlan/useInterviewPlanHasInterviewHiddenFromCandidate';
import useIsInterviewPlanEntityValid from 'src/entities/InterviewPlan/useIsInterviewPlanValid';

import CreateTaskStep from './CreateTaskStep';
import { TaskCreateInputWrapper } from './types';

interface Props {
  title: string;
  input: TaskCreateInputWrapper;
  interviewPlanLoading: boolean;
  source: TaskCreationSource;
  setInput: (input: TaskCreateInputWrapper) => void;
  onContinue: (input: TaskCreateInputWrapper) => void;
  onClose: () => void;
  allowEmptyPlan?: boolean;
  onJobStageIdChange?: (jobStageId: string) => void;
  onInterviewPlanChange: (interviewPlan: CreateTaskDialogPage_InterviewPlanFragment) => void;
}

type Fragments = {
  application: CreateTaskDialogPage_ApplicationFragment | undefined;
  interviewPlan: CreateTaskDialogPage_InterviewPlanFragment | undefined;
};

const CreateTaskDialogPage: FCWithFragments<Fragments, Props> = ({
  allowEmptyPlan = false,
  title,
  input,
  interviewPlan,
  interviewPlanLoading,
  application,
  source,
  onClose,
  onContinue,
  onJobStageIdChange,
  onInterviewPlanChange,
  setInput,
}) => {
  const { t } = useTranslation();
  const [isImportingAvailability, setIsImportingAvailability] = useState(false);

  const {
    isValid,
    error: interviewPlanError,
    title: interviewPlanErrorTitle,
  } = useIsInterviewPlanEntityValid(
    { jobStage: interviewPlan || { id: input.customJobStageId, jobStageInterviewGroups: [] } },
    {
      isDayBreakAllowed: source === TaskCreationSource.CandidateAvailabilityRequest,
      allowEmptyPlan,
      allowEmptyInterviews: allowEmptyPlan,
      allowAllInterviewsHiddenFromCandiate: true,
      allowDynamicBreaks: source !== TaskCreationSource.SelfScheduleRequest,
    }
  );

  // If customJobStageId is null, we are loading the interview plan
  const hasInterviewPlanLoaded = !interviewPlanLoading;

  const hasInterviewHiddenFromCandidate = useInterviewPlanEntityHasInterviewHiddenFromCandidate({
    interviewPlan,
  });

  const submitOptionTooltip = useMemo(() => {
    if (!isValid) {
      return `${interviewPlanErrorTitle} 𐄁 ${interviewPlanError}`;
    }
    if (hasInterviewHiddenFromCandidate && source === TaskCreationSource.SelfScheduleRequest) {
      return t('hiring-dashboard-tasks:tasks.create.selfScheduleUnavailableTooltip');
    }
    return undefined;
  }, [hasInterviewHiddenFromCandidate, interviewPlanError, interviewPlanErrorTitle, isValid, source, t]);

  return (
    <DialogStep
      title={title}
      subTitle={t('hiring-dashboard-tasks:tasks.create.stepSubtitle')}
      cancelOptions={{
        label: t('hiring-dashboard-common:common.cancel'),
        onClick: onClose,
      }}
      submitOptions={{
        dataTestId: 'schedule-task-create-continue-button',
        isDisabled:
          !isValid ||
          !hasInterviewPlanLoaded ||
          (hasInterviewHiddenFromCandidate && source === TaskCreationSource.SelfScheduleRequest) ||
          isImportingAvailability,
        tooltip: submitOptionTooltip,
        label: t('hiring-dashboard-tasks:tasks.create.continue'),
        onClick: () => {
          onContinue(input);
        },
      }}
      onClose={onClose}
    >
      {!application && <CircularProgress size={32} sx={{ display: 'flex', margin: 'auto' }} />}
      {application && (
        <CreateTaskStep
          application={application}
          jobStage={interviewPlan}
          jobStageLoading={interviewPlanLoading}
          onInputChange={(updatedInput) => {
            if (updatedInput.jobStageId !== input.jobStageId) {
              onJobStageIdChange?.(updatedInput.jobStageId);
            }
            setInput(updatedInput);
          }}
          input={input}
          isAvailabilityEnabled={false}
          source={source}
          setIsImportingAvailability={setIsImportingAvailability}
          onInterviewPlanChange={onInterviewPlanChange}
        />
      )}
    </DialogStep>
  );
};

CreateTaskDialogPage.fragments = {
  application: gql`
    ${CreateTaskStep.fragments.application}
    fragment CreateTaskDialogPage_application on Application {
      id
      ...CreateTaskStep_application
      jobStage {
        id
      }
      candidate {
        id
        timezone
      }
    }
  `,
  interviewPlan: gql`
    ${CreateTaskStep.fragments.jobStage}
    ${cloneInterviewPlan.fragments.interviewPlan}
    ${useIsInterviewPlanEntityValid.fragments.jobStage}
    ${useInterviewPlanEntityHasInterviewHiddenFromCandidate.fragments.interviewPlan}

    fragment CreateTaskDialogPage_interviewPlan on JobStage {
      id
      ...CreateTaskStep_jobStage
      ...cloneInterviewPlan_interviewPlan
      ...useIsInterviewPlanValid_jobStage
      ...useInterviewPlanHasInterviewHiddenFromCandidate_interviewPlan
    }
  `,
};

export default CreateTaskDialogPage;
